import { PurchaseParamsStruct } from "@lib/typechain/ProductsModule"
import { BigNumber } from "ethers"
import { ProductCart } from "@src/lib/cart/cartProductTypes"
import { quoteParams } from "@lib/useEthUsd"
import { constants } from "@src/constants"
import PriceFeed from "@src/lib/artifacts/contracts/PriceFeed.sol/PriceFeed"
import ProductsModule from "@src/lib/artifacts/contracts/ProductsModule.sol/ProductsModule"
import { zeroAddress } from "viem"
import { readContract, signTypedData } from "@wagmi/core"
import { getVRS } from "@src/lib/getVRS"
import { generateNonce } from "../../generateNonce"
import fetcher from "../../fetcher"
import { getSliceSubdomain } from "../.."

export type NewTransaction = {
  description: string
  hash: string
  confirmations?: number
}

export const payProductsWithAuthorizationConfig = async (
  buyer: `0x${string}`,
  productData: ProductCart[],
  chainId: number,
  authorizationParams: Record<string, string | number>
) => {
  let ethUsd: BigInt
  const isUsd = productData.findIndex((product) => product.isUSD === true)

  if (isUsd !== -1) {
    const quote = await readContract({
      address: constants[chainId].addresses["PriceFeed"],
      abi: PriceFeed.abi,
      functionName: "getQuote",
      args: quoteParams(chainId)
    })
    ethUsd = quote as BigInt // Chainlink: Number(quote[1])
  }

  let ethAmount: BigNumber
  let purchaseParams: PurchaseParamsStruct[] = []

  try {
    productData.forEach((product) => {
      const {
        slicerId,
        productId,
        quantity,
        price,
        currency,
        isUSD,
        extCallValue,
        buyerCustomData
      } = product

      const currentPrice = ethAmount || 0

      purchaseParams.push({
        buyer,
        slicerId,
        quantity,
        currency,
        productId,
        buyerCustomData
      })

      if (currency === zeroAddress) {
        const productPrice = isUSD
          ? BigNumber.from(price)
              .mul(BigNumber.from(10).pow(18)) // chainlink -> pow(24)
              .mul(102) // 2% overpayment to compensate for price fluctuations (repaid to buyer during tx)
              .div(100)
              .div(BigNumber.from(ethUsd))
              .add(extCallValue)
          : BigNumber.from(price).add(extCallValue)
        ethAmount = BigNumber.from(currentPrice).add(productPrice)
      }
    })

    return {
      config: {
        address: constants[chainId].addresses["ProductsModule"],
        abi: ProductsModule.abi,
        functionName: "payWithAuthorization",
        args: [purchaseParams, authorizationParams],
        value: ethAmount
      },
      purchaseParams
    }
  } catch (err) {
    console.log(err)

    throw err
  }
}

const signAuthorization = async (
  sender: `0x${string}`,
  chainId: number,
  message: {
    from: `0x${string}`
    to: `0x${string}`
    value: bigint
    validAfter: bigint
    validBefore: bigint
    nonce: `0x${string}`
  }
) => {
  // All properties on a domain are optional
  const domain = {
    name: "USD Coin",
    version: "2",
    chainId,
    verifyingContract: constants[chainId].addresses["USDCPriceFeed"]
  } as const

  const types = {
    TransferWithAuthorization: [
      { name: "from", type: "address" },
      { name: "to", type: "address" },
      { name: "value", type: "uint256" },
      { name: "validAfter", type: "uint256" },
      { name: "validBefore", type: "uint256" },
      { name: "nonce", type: "bytes32" }
    ]
  } as const

  return await signTypedData({
    account: sender,
    domain,
    message,
    primaryType: "TransferWithAuthorization",
    types
  })
}

export const payWithAuthorization = async (
  sender: `0x${string}`,
  totalUsdcPrice: number,
  buyer: `0x${string}`,
  productData: ProductCart[],
  chainId: number
) => {
  try {
    const currentTimestamp = Math.floor(Date.now() / 1000)
    const authorizationParams = {
      from: sender,
      to: constants[chainId].addresses["ProductsModule"],
      value: BigInt(totalUsdcPrice),
      validAfter: BigInt(currentTimestamp),
      validBefore: BigInt(currentTimestamp + 3600),
      nonce: generateNonce()
    } as const

    const signature = await signAuthorization(
      sender,
      chainId,
      authorizationParams
    )
    const { v, r, s } = getVRS(signature)
    authorizationParams["v"] = v
    authorizationParams["r"] = r
    authorizationParams["s"] = s

    const { purchaseParams } = await payProductsWithAuthorizationConfig(
      buyer,
      productData,
      chainId,
      authorizationParams as any
    )

    // TODO: What condition to use here?
    let hash: `0x${string}`
    const { hash: txHash } = await fetcher(
      `https://${getSliceSubdomain(chainId)}slice.so/api/slice_relay`,
      {
        method: "POST",
        body: JSON.stringify(
          {
            args: [purchaseParams, authorizationParams]
          },
          (key, value) => (typeof value === "bigint" ? value.toString() : value)
        )
      }
    )
    hash = txHash

    // ALT: Use account wallet
    // const { request } = await prepareWriteContract(config)
    // const { hash: txHash } = await writeContract(request)
    // hash = txHash

    return hash
  } catch (err) {
    console.log(err)

    throw err
  }
}

export default payWithAuthorization
